import Head from "next/head";
import Image from "next/image";
import {
  CLMButtonPrimary,
  CLMButtonSecondaryBorder,
} from "components/CLM/v3/Buttons";
import AnimatedLogos from "components/customers/AnimatedLogos";
import { BASE_ASSETS_URL } from "@constants/constants";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import RequestDemo from "components/CLM/RequestDemo";
import FAQ2 from "components/CLM/FAQ2";
import CTASection from "components/CLM/CTASection";
import { HiOutlineKey } from "react-icons/hi";
import { MdLibraryBooks } from "react-icons/md";

const companyLogos = [
  {
    url: `${BASE_ASSETS_URL}/case-studies/angelfire.png`,
    name: "Angelfire",
    haveCaseStudy: true,
    companyKey: "angel-fire".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/transport.png`,
    name: "G & H Transport",
    haveCaseStudy: true,
    companyKey: "g-h-transport".toLowerCase(),
  },

  {
    url: `${BASE_ASSETS_URL}/case-studies/beaver.png`,
    name: "Beaver",
    haveCaseStudy: true,
    companyKey: "beaver".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/shine-above.png`,
    name: "Shine Above",
    haveCaseStudy: true,
    companyKey: "shine-above".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/harry-corry.png`,
    name: "Harry Corry",
    haveCaseStudy: true,
    companyKey: "harry-corry-ltd".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/rethink.png`,
    name: "Rethink",
    haveCaseStudy: true,
    companyKey: "rethink".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/mancosa.png`,
    name: "Mancosa",
    haveCaseStudy: true,
    companyKey: "mancosa".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/aging-comfort.png`,
    name: "Aging Comfort",
    haveCaseStudy: true,
    companyKey: "aging-comfort".toLowerCase(),
  },

  // {
  //   url: require("assets/new-case-studies/benavest.png"),
  //   name: "Benavest",
  //   haveCaseStudy: true,
  //   companyKey: "benavest".toLowerCase(),
  // },
];

const workflowFaqData = [
  {
    question: "What is BoloSign’s Workflow Manager?",
    answer:
      "BoloSign’s Workflow Manager is a no-code interface that automates your contract process from draft to signature. Instead of emailing documents back and forth, you draw a simple approval flow—defining who drafts, who reviews, and who signs—and BoloSign handles the routing automatically.",
  },
  {
    question: "How does Workflow Manager speed up contract management?",
    answer:
      "By enforcing your predefined approval rules, Workflow Manager ensures every draft goes to the right people without manual handoffs. You always see exactly which stage a contract is in, and automated reminders prevent bottlenecks so deals close faster and with fewer errors.",
  },
  {
    question: "What workflow types can I create?",
    answer:
      "You can choose from Template Workflows, where pre-approved contract templates auto-populate key fields, or Custom Workflows, where you upload existing Word/PDF documents and add signature or input fields. This flexibility lets you standardize recurring agreements while still handling unique, externally drafted contracts.",
  },
  {
    question: "How are approvals automated?",
    answer:
      "Once you define a sequence—such as Legal → Finance → Manager—BoloSign immediately sends the draft to each approver in turn. Each reviewer receives a notification, can comment or request edits, and the system won’t move forward until every step is completed, guaranteeing a full audit trail.",
  },
  {
    question: "Can I integrate Workflow Manager with other tools?",
    answer:
      "Yes. BoloSign links with CRMs (Salesforce, HubSpot) so deal data auto-fills into contracts, cloud storage (Google Drive, OneDrive) to sync template changes, and automation platforms (Zapier, Make) for custom triggers like generating contracts when a deal reaches a certain stage. This keeps your data flowing smoothly across systems.",
  },
  {
    question: "How do I keep templates up to date?",
    answer:
      "Designate one “master” template for each contract type. When you update that master, any new contract pulls the latest version. If someone opens an older draft, BoloSign flags it and lets them migrate to the current template with a single click, so everyone always works from the same content.",
  },
  {
    question: "What role do intake forms play?",
    answer:
      "Intake forms collect all the essential details—like client name, payment terms, or project scope—before drafting begins. Those answers auto-populate into your chosen template, ensuring drafts start complete and reducing back-and-forth edits for missing information.",
  },
];

const ContractWorkflow = () => {
  return (
    <>
      <Head>
        <title>Contract Workflow Automation | BoloSign CLM Software</title>
        <meta
          name="description"
          content="Automate your entire contract process with BoloSign Workflow Manager. Create no-code workflows for drafting, approvals, and signatures to speed up deal closures."
        />
        <meta
          name="keywords"
          content="Contract workflow automation, BoloSign Workflow Manager, no-code contract workflows, automated contract approvals, contract drafting automation, CLM workflow software"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/workflow/" />
        <meta
          property="og:url"
          content="https://www.bolosign.com/clm/workflow/"
        />
      </Head>
      {/* section 1 */}
      <section className="bg-dark text-white w-full xl:h-[min(750px,calc(100vh-110px))] grid place-items-center">
        <div className="max-w-screen-xl w-full h-full px-2 sm:px-4 md:px-8 lg:px-0 xl:px-8 flex flex-col lg:flex-row items-center justify-center gap-4">
          <div className="flex-1 py-6 sm:py-10 text-center lg:text-left">
            <div className="w-full max-w-screen-mxl mx-auto px-4 lg:px-8 xxl:px-0 flex flex-col sm:gap-5 lg:gap-3 xl:gap-4">
              <p className="w-full sm:text-2xl font-medium">
                BoloSign Workflow
              </p>
              <h1 className="text-4xl sm:text-6xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent">
                  Automate{" "}
                </span>
                <span className="bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent">
                  Every{" "}
                </span>
                <span className="bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent">
                  Step{" "}
                </span>
                of your contract process
              </h1>

              <p className="w-full text-lg max-w-screen-md mx-auto">
                Create contract workflows that automate drafting, approvals, and
                signatures — no code or legal expertise needed.
              </p>
              <div className="flex items-center justify-center lg:justify-start flex-wrap gap-6 mt-4">
                <CLMButtonPrimary
                  buttonText="Contact Sales"
                  // icon={<HiOutlineKey />}
                  buttonUrl="request-demo"
                />
                {/* <CLMButtonSecondaryBorder
                  buttonText="Documentation"
                  icon={<MdLibraryBooks />}
                  buttonUrl="https://developer.boloforms.com/"
                /> */}
              </div>
            </div>
          </div>

          <div className="flex flex-1 py-6 justify-end lg:pr-7 xl:pr-6 px-2 md:px-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/workflow-hero-1.png`}
              alt="hero"
              width={400}
              height={400}
              className="w-full object-contain sm:scale-110 lg:scale-100 xl:scale-110 h-full aspect-square max-w-md lg:max-w-lg max-h-[min(600px,calc(100vh-200px))] xl:min-h-[450px] rounded-xl overflow-hidden"
            />
          </div>
        </div>
      </section>
      {/* <section className="  md:pb-16 mt-16 md:mt-0 px-12 bg-black">
        <div className=" flex items-start justify-center">
          <div className="flex-1 text-white flex flex-col xl:gap-12 gap-5 lg:mt-12 xl:mt-16 mt-6">
            <p className="text-2xl font-semibold">BoloSign Workflow</p>
            <h2 className=" xl:text-6xl md:text-5xl text-3xl font-semibold">
              <span className="text-purple-200 font-bold">
                Automate Every Step
              </span>{" "}
              <br /> of Your Contract Process
            </h2>
            <p className="lg:text-2xl text-xl">
              Create contract workflows that automate drafting, approvals, and
              signatures — no code or legal expertise needed.
            </p>
            <div className="flex gap-6">
              <CLMButtonPrimary
                buttonText="Contact Sales"
                buttonUrl="clm/request-demo"
              />
            </div>
          </div>
          <div className="flex-1">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/workflow-hero.png`}
              alt="workflow"
              width={500}
              height={500}
              className=""
            />
          </div>
        </div>
      </section> */}

      <section className="py-10 bg-dark">
        <p className="text-2xl font-semibold text-center text-white mb-8">
          Trusted By
        </p>
        <AnimatedLogos />
      </section>

      <EqualDivisionSection
        // title=""
        heading="Always generate accurate contracts using smart templates"
        description="Auto-fill approved templates using simple form inputs—no edits, no errors."
        checks={[
          "Team or clients fill out a quick form.",
          "BoloSign picks the right template.",
          "Fields auto-populate instantly.",
          "The final contract is ready to send, error-free.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/questionnaire.png`}
        bgColor="bg-cream"
        imageRight={true}
      />

      <EqualDivisionSection
        // title=""
        heading="Create contracts within your team’s favorite tools"
        description="Generate and manage contracts directly from Pipedrive, Airtable, and HubSpot—no switching tabs."
        checks={[
          "Auto-fill deal data – Pull details straight from your CRM.",
          "Request approvals – Route for sign-off without email threads.",
          "Share & track – Send contracts and monitor progress in real time.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/opportunities.png`}
        bgColor="bg-transparent"
        imageRight={false}
      />

      <EqualDivisionSection
        heading="Collect contract details upfront, no follow-ups needed"
        description="Use smart intake forms to gather key info the moment a contract is uploaded—so reviews move faster."
        checks={[
          "Smart forms – Capture the right details from the start.",
          "Linked responses – Auto-attach info to each contract.",
          "Instant context – No more chasing missing data.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/intake.png`}
        bgColor="bg-cream"
        imageRight={true}
      />

      <EqualDivisionSection
        // title=""
        heading="Reduce time for contract approvals"
        description="Automate contract reviews with smart routing and custom workflows."
        checks={[
          "Custom rules – Route by deal size, role, or type.",
          "Stay on track – No more bottlenecks or missed steps.",
          "Real-time visibility – Monitor progress and act fast.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/approval-step.png`}
        bgColor="bg-transparent"
        imageRight={false}
      />

      <RequestDemo
        title="Get ready to automate your contracting processes"
        btnText="Contact Sales"
        bgColor="bg-dark"
        btnBgColor="bg-purple-500"
        btnTextColor="text-white"
      />

      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={workflowFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default ContractWorkflow;
